import type {
  BespokeTemplateResponse,
  BespokeTemplateStatus,
  FormSchemaVersion,
} from '../../services/api/types.gen';
import type { RogerDocFormType } from '../../services/api/models/RogerDocFormType';
import { inferDeployStageLabel } from '../../utils/deployStage';
import { downloadBlobAsFile } from '../../utils/downloadFile';
import {
  formSchemaVersionPinKey,
  formSchemaVersionPinKeyFromTemplate,
  pinsFromFormSchemaVersions,
} from './formSchemaUtils';

/** Bump when breaking the export envelope shape or template row fields. */
export const BESPOKE_TEMPLATES_EXPORT_FORMAT_VERSION = 5;

export type BespokeTemplateExportSourceRow = BespokeTemplateResponse;

/**
 * One bespoke row for portability (ids/timestamps omitted — recreated on import).
 * Environment-wide; legacy bundles (v≤3) may include per-row agency keys which importers ignore.
 * v5+ rows carry the full RogerDoc pin (`form_type` + `form_schema_version`) so
 * import/export no longer need to infer form type from `schema_version_name`,
 * which is ambiguous when multiple form types share a schema version name.
 */
export type BespokeTemplateExportRow = {
  form_type: RogerDocFormType;
  schema_version_name: string;
  form_schema_version: string;
  section_name: string;
  layout_json?: unknown | null;
  name: string;
  description?: string | null;
  status: BespokeTemplateStatus;
};

export type BespokeTemplatesExportBundle = {
  format_version: typeof BESPOKE_TEMPLATES_EXPORT_FORMAT_VERSION;
  /**
   * ``global`` (v4+). Older bundles used ``single_agency`` / ``all_agencies`` metadata only.
   */
  export_scope: 'global' | 'all_agencies' | 'single_agency';
  exported_at: string;
  selected_form_types: RogerDocFormType[];
  templates: BespokeTemplateExportRow[];
};

function schemaVersionDisplayName(version: FormSchemaVersion): string {
  return version.name.trim();
}

/**
 * Allowed schema version pins for the given form types (from the live catalog).
 */
export function collectFormSchemaVersionPinsForFormTypes(params: {
  formTypes: Iterable<RogerDocFormType>;
  versionsByFormType: Map<RogerDocFormType, FormSchemaVersion[]>;
}): Set<string> {
  const keys = new Set<string>();
  for (const ft of params.formTypes) {
    for (const pin of pinsFromFormSchemaVersions(
      ft,
      params.versionsByFormType.get(ft) ?? []
    )) {
      keys.add(formSchemaVersionPinKey(pin));
    }
  }
  return keys;
}

/**
 * @deprecated Use collectFormSchemaVersionPinsForFormTypes for pin-aware filtering.
 * Kept for validation job name checks that only need schema display names.
 */
export function collectSchemaVersionNamesForFormTypes(params: {
  formTypes: Iterable<RogerDocFormType>;
  versionsByFormType: Map<RogerDocFormType, FormSchemaVersion[]>;
}): Set<string> {
  const names = new Set<string>();
  for (const ft of params.formTypes) {
    const vers = params.versionsByFormType.get(ft) ?? [];
    for (const v of vers) {
      const n = schemaVersionDisplayName(v);
      if (n) names.add(n);
    }
  }
  return names;
}

/** Stable sort/dedup key for a pinned export row. */
function exportRowSortKey(row: BespokeTemplateExportRow): string {
  return [
    row.form_type,
    row.schema_version_name,
    row.form_schema_version,
    row.section_name,
    row.name,
  ].join('\0');
}

/**
 * Filters bespoke rows to those whose full schema version pin is in the allowlist.
 */
export function filterBespokeTemplatesForExport(params: {
  allTemplates: BespokeTemplateExportSourceRow[];
  allowedFormSchemaVersionPinKeys: Set<string>;
}): BespokeTemplateExportRow[] {
  const rows: BespokeTemplateExportRow[] = [];
  for (const t of params.allTemplates) {
    const key = formSchemaVersionPinKeyFromTemplate(t);
    if (!params.allowedFormSchemaVersionPinKeys.has(key)) continue;
    rows.push({
      form_type: t.form_type as RogerDocFormType,
      schema_version_name: t.schema_version_name,
      form_schema_version: t.form_schema_version,
      section_name: t.section_name,
      layout_json: t.layout_json,
      name: t.name,
      description: t.description,
      status: t.status,
    });
  }
  return rows.sort((a, b) =>
    exportRowSortKey(a).localeCompare(exportRowSortKey(b), undefined, {
      sensitivity: 'base',
    })
  );
}

export function buildBespokeTemplatesExportBundle(params: {
  selectedFormTypes: RogerDocFormType[];
  versionsByFormType: Map<RogerDocFormType, FormSchemaVersion[]>;
  allTemplates: BespokeTemplateExportSourceRow[];
  exportedAt?: Date;
}): BespokeTemplatesExportBundle {
  const allowed = collectFormSchemaVersionPinsForFormTypes({
    formTypes: params.selectedFormTypes,
    versionsByFormType: params.versionsByFormType,
  });
  const templates = filterBespokeTemplatesForExport({
    allTemplates: params.allTemplates,
    allowedFormSchemaVersionPinKeys: allowed,
  });
  return {
    format_version: BESPOKE_TEMPLATES_EXPORT_FORMAT_VERSION,
    export_scope: 'global',
    exported_at: (params.exportedAt ?? new Date()).toISOString(),
    selected_form_types: [...params.selectedFormTypes].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    ),
    templates,
  };
}

/** Distinct `form_type`s present on the chosen rows — read straight off the pin. */
function collectFormTypesForBundleMetadata(
  templates: BespokeTemplateExportRow[]
): RogerDocFormType[] {
  const out = new Set<RogerDocFormType>();
  for (const t of templates) {
    out.add(t.form_type);
  }
  return [...out].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' })
  );
}

/**
 * Builds a download bundle using an explicit subset of normalized template rows (e.g. UI-checked leaves).
 */
export function buildBespokeTemplatesExportBundleFromChosenTemplates(params: {
  templates: BespokeTemplateExportRow[];
  exportedAt?: Date;
}): BespokeTemplatesExportBundle {
  const sorted = [...params.templates].sort((a, b) =>
    exportRowSortKey(a).localeCompare(exportRowSortKey(b), undefined, {
      sensitivity: 'base',
    })
  );
  return {
    format_version: BESPOKE_TEMPLATES_EXPORT_FORMAT_VERSION,
    export_scope: 'global',
    exported_at: (params.exportedAt ?? new Date()).toISOString(),
    selected_form_types: collectFormTypesForBundleMetadata(sorted),
    templates: sorted,
  };
}

/** Safe segment for filenames (Windows-safe subset). */
export function sanitizeFilenameSegment(raw: string): string {
  return (
    raw
      .trim()
      .replace(/[^\w-]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '') || 'bespoke'
  );
}

/**
 * Deploy / environment label for export filenames.
 * Shared inference via ``inferDeployStageLabel``, then sanitized for paths.
 */
export function inferExportStageLabel(): string {
  return sanitizeFilenameSegment(inferDeployStageLabel());
}

export function buildBespokeExportFilename(params: {
  exportedAt?: Date;
}): string {
  const envSeg = inferExportStageLabel();
  const stamp = (params.exportedAt ?? new Date())
    .toISOString()
    .replace(/[:.]/g, '-');
  return `bespoke-templates-export-${envSeg}-${stamp}.json`;
}

export function downloadJsonFile(filename: string, data: unknown): void {
  let serialized: string;
  try {
    serialized = JSON.stringify(data, null, 2);
  } catch (err) {
    throw new Error(`Failed to serialize export data: ${String(err)}`);
  }
  downloadBlobAsFile(serialized, filename, 'application/json');
}
